import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { tasksService } from '../../services';
import CommunicationLogger from './CommunicationLogger';
import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';
import './TasksDashboard.css';

const TasksDashboard = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showCommLogger, setShowCommLogger] = useState(false);
  const [commLoggerTask, setCommLoggerTask] = useState(null);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [selectedFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (selectedFilter === 'pending') filters.status = 'pending';
      if (selectedFilter === 'in_progress') filters.status = 'in_progress';
      if (selectedFilter === 'completed') filters.status = 'completed';
      if (selectedFilter === 'overdue') filters.overdue_only = 'true';
      if (selectedFilter === 'due_soon') filters.due_soon = 'true';
      if (selectedFilter === 'high_priority') filters.priority = 'high';

      const data = await tasksService.getTasks(filters);
      setTasks(data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await tasksService.getTaskStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch task statistics:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await tasksService.completeTask(taskId);
      setSuccess('Task completed successfully');
      await fetchTasks();
      await fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to complete task:', error);
      setError('Failed to complete task. Please try again.');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await tasksService.updateTask(taskId, updates);
      setSuccess('Task updated successfully');
      await fetchTasks();
      await fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to update task:', error);
      setError('Failed to update task. Please try again.');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await tasksService.deleteTask(taskId);
      setSuccess('Task deleted successfully');
      await fetchTasks();
      await fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to delete task:', error);
      setError('Failed to delete task. Please try again.');
    }
  };

  const handleTaskCreated = async (newTask) => {
    setSuccess('Task created successfully');
    await fetchTasks();
    await fetchStats();
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleTaskUpdated = async (updatedTask) => {
    setSuccess('Task updated successfully');
    setEditingTask(null);
    await fetchTasks();
    await fetchStats();
    setTimeout(() => setSuccess(null), 3000);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-gray-600 bg-gray-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getUrgencyColor = (urgencyStatus) => {
    switch (urgencyStatus) {
      case 'overdue': return 'border-l-4 border-red-500 bg-red-50';
      case 'due_soon': return 'border-l-4 border-yellow-500 bg-yellow-50';
      default: return 'border-l-4 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Task Management</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage your client tasks and workflow automation
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Create Task
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">{stats.total_tasks || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Tasks</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.total_tasks || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-semibold text-sm">{stats.pending_tasks || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.pending_tasks || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-semibold text-sm">{stats.overdue_tasks || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Overdue</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.overdue_tasks || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold text-sm">{stats.completed_tasks || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.completed_tasks || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'all', label: 'All Tasks', count: stats.total_tasks },
            { key: 'pending', label: 'Pending', count: stats.pending_tasks },
            { key: 'in_progress', label: 'In Progress', count: stats.in_progress_tasks },
            { key: 'overdue', label: 'Overdue', count: stats.overdue_tasks },
            { key: 'due_soon', label: 'Due Soon', count: stats.due_soon_tasks },
            { key: 'high_priority', label: 'High Priority', count: stats.high_priority_tasks },
            { key: 'completed', label: 'Completed', count: stats.completed_tasks }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setSelectedFilter(filter.key)}
              className={`${
                selectedFilter === filter.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            >
              {filter.label}
              {filter.count > 0 && (
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  selectedFilter === filter.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {filter.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tasks List */}
      {tasks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {selectedFilter === 'all' 
              ? 'Get started by creating a new task or moving clients through workflow stages.'
              : `No ${selectedFilter.replace('_', ' ')} tasks at the moment.`}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {tasks.map((task) => (
              <li key={task.id} className={`${getUrgencyColor(task.urgency_status)}`}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {task.title}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                        <span>
                          <strong>Client:</strong> {task.client_first_name} {task.client_last_name}
                        </span>
                        {task.workflow_stage_name && (
                          <span>
                            <strong>Stage:</strong> {task.workflow_stage_name}
                          </span>
                        )}
                        {task.category && (
                          <span>
                            <strong>Category:</strong> {task.category}
                          </span>
                        )}
                      </div>

                      {task.description && (
                        <p className="mt-2 text-sm text-gray-600">{task.description}</p>
                      )}

                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <span>
                          <strong>Due:</strong> {formatDate(task.due_date)}
                        </span>
                        {task.urgency_status === 'overdue' && (
                          <span className="ml-2 text-red-600 font-medium">OVERDUE</span>
                        )}
                        {task.urgency_status === 'due_soon' && (
                          <span className="ml-2 text-yellow-600 font-medium">DUE SOON</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {task.status !== 'completed' && (
                        <>
                          <button
                            onClick={() => handleCompleteTask(task.id)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            Complete
                          </button>
                          <button
                            onClick={() => {
                              setCommLoggerTask(task);
                              setShowCommLogger(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                          >
                            Log Communication
                          </button>
                          <button
                            onClick={() => setEditingTask(task)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Edit
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteTask(task.id)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Communication Logger Modal */}
      {showCommLogger && commLoggerTask && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CommunicationLogger
              clientId={commLoggerTask.client_id}
              taskId={commLoggerTask.id}
              workflowStageId={commLoggerTask.workflow_stage_id}
              onClose={() => {
                setShowCommLogger(false);
                setCommLoggerTask(null);
              }}
              onSaved={() => {
                setShowCommLogger(false);
                setCommLoggerTask(null);
                setSuccess('Communication logged successfully');
                setTimeout(() => setSuccess(null), 3000);
              }}
            />
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTaskCreated={handleTaskCreated}
      />

      {/* Edit Task Modal */}
      <EditTaskModal
        isOpen={!!editingTask}
        task={editingTask}
        onClose={() => setEditingTask(null)}
        onTaskUpdated={handleTaskUpdated}
      />
    </div>
  );
};

export default TasksDashboard;